import {
  isFailedAnalyzeExternalWebTool,
  isIncompleteAnalyzeExternalWebTool,
  isSiteScopeWebAnalysisToolName,
  readAnalyzeExternalWebFailureMessage,
} from '@/lib/assistant/analyze-external-web-result-contract';
import { isInformationalAssistantTool } from '@/lib/assistant/tool-ui';
import type { Message } from '@/types/assistant';

export const NO_RESPONSE_RECORDED_FALLBACK_TEXT =
  'No response has been recorded for this request. Please try again.';

export const REQUEST_CANCELLED_TEXT = '[Request cancelled by user]';
export const ASSISTANT_REQUEST_FAILED =
  'I ran into an issue while completing that request. Please try again.';
export const ASSISTANT_EDIT_VALIDATION_FAILED =
  "I couldn't apply that edit cleanly. Please try again.";
export const SYNTHETIC_ASSISTANT_CONFIRMATION = 'Changes applied.';
export const SYNTHETIC_ASSISTANT_PARTIAL_CONFIRMATION = 'Some changes were applied.';
export const SYNTHETIC_ASSISTANT_INCOMPLETE_CONFIRMATION =
  "The request didn't finish. Please try again.";
export const ASSISTANT_RATE_LIMITED =
  'Too many requests right now. Please wait a moment and try again.';
export const ASSISTANT_UNAVAILABLE = 'AI is temporarily unavailable right now. Please try again.';
export const ASSISTANT_AUTH_CONFIGURATION_ERROR =
  'AI is not configured correctly right now. Please contact support.';
export const SITE_ASSISTANT_REQUEST_FAILED =
  'Something went wrong. Please try again after a little while.';
const ASSISTANT_ERROR_PREFIX = 'I ran into an issue while completing that request:';

const ASSISTANT_EXACT_ERROR_MESSAGES = new Set<string>([
  ASSISTANT_EDIT_VALIDATION_FAILED,
  ASSISTANT_RATE_LIMITED,
  ASSISTANT_UNAVAILABLE,
  ASSISTANT_AUTH_CONFIGURATION_ERROR,
]);

const ASSISTANT_DISPLAY_ERROR_MESSAGES = new Set<string>([
  ASSISTANT_REQUEST_FAILED,
  SITE_ASSISTANT_REQUEST_FAILED,
  SYNTHETIC_ASSISTANT_INCOMPLETE_CONFIRMATION,
  ...ASSISTANT_EXACT_ERROR_MESSAGES,
]);

const SITE_ASSISTANT_PUBLIC_ERROR_MESSAGES = new Set<string>([
  ASSISTANT_RATE_LIMITED,
  ASSISTANT_UNAVAILABLE,
  ASSISTANT_AUTH_CONFIGURATION_ERROR,
]);

export function getSiteAssistantPublicErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) {
    return SITE_ASSISTANT_REQUEST_FAILED;
  }

  if (SITE_ASSISTANT_PUBLIC_ERROR_MESSAGES.has(error.message)) {
    return error.message;
  }

  return SITE_ASSISTANT_REQUEST_FAILED;
}

export function resolveAssistantErrorMessage(detail?: string): string {
  const normalizedDetail = detail?.trim();
  if (!normalizedDetail) {
    return ASSISTANT_REQUEST_FAILED;
  }

  if (ASSISTANT_EXACT_ERROR_MESSAGES.has(normalizedDetail)) {
    return normalizedDetail;
  }

  return `${ASSISTANT_ERROR_PREFIX} ${normalizedDetail}`;
}

export function isAssistantErrorMessage(content?: string): boolean {
  const normalizedContent = content?.trim();
  if (!normalizedContent) {
    return false;
  }

  return (
    ASSISTANT_DISPLAY_ERROR_MESSAGES.has(normalizedContent) ||
    normalizedContent.startsWith(ASSISTANT_ERROR_PREFIX)
  );
}

export function ensureAssistantReply(messages: ReadonlyArray<Message>, content: string): Message[] {
  const updated = [...messages];
  const lastUserIndex = findLastUserMessageIndex(updated);
  const hasAssistantReply = hasAssistantReplyAfterUser(updated, lastUserIndex);

  if (!hasAssistantReply) {
    updated.push({
      role: 'assistant',
      content,
      timestamp: Date.now(),
      streaming: false,
    });
  }

  return updated;
}

export function findLastUserMessageIndex(messages: ReadonlyArray<{ role: string }>): number {
  for (let index = messages.length - 1; index >= 0; index--) {
    if (messages[index]?.role === 'user') {
      return index;
    }
  }

  return -1;
}

/** Remove empty assistant rows after the latest user message in the active turn. */
export function removeEmptyAssistantMessagesInActiveTurn(
  messages: ReadonlyArray<Message>,
): Message[] {
  const lastUserIndex = findLastUserMessageIndex(messages);
  if (lastUserIndex === -1) {
    return messages as Message[];
  }

  let changed = false;
  const next = messages.filter((message, index) => {
    if (index <= lastUserIndex) {
      return true;
    }

    const remove = message.role === 'assistant' && !message.content.trim();
    if (remove) {
      changed = true;
    }

    return !remove;
  });

  return changed ? next : (messages as Message[]);
}

export function hasAssistantReplyAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): boolean {
  for (let index = lastUserIndex + 1; index < messages.length; index++) {
    const message = messages[index];
    if (!message || message.role !== 'assistant') {
      continue;
    }

    if (message.content.trim().length > 0) {
      return true;
    }
  }

  return false;
}

/** True if a non-empty assistant reply appears before the next user message. */
export function hasAssistantReplyBeforeNextUser(
  messages: ReadonlyArray<Message>,
  afterIndex: number,
): boolean {
  for (let index = afterIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (message?.role === 'user') {
      return false;
    }

    if (message?.role === 'assistant' && message.content.trim().length > 0) {
      return true;
    }
  }

  return false;
}

export function resolveTurnAssistantReplyContent(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): string | undefined {
  if (lastUserIndex === -1) {
    return undefined;
  }

  let hasSuccessfulMutationTool = false;
  let lastToolError: string | null = null;

  for (let index = lastUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (!message || message.role !== 'tool') {
      continue;
    }

    if (message.toolError) {
      lastToolError = message.toolError;
      continue;
    }

    const analyzeExternalWebFailure = readAnalyzeExternalWebFailureMessage(
      message.toolResult?.rawOutput,
    );
    if (analyzeExternalWebFailure) {
      lastToolError = analyzeExternalWebFailure;
      continue;
    }

    if (message.toolResult === undefined) {
      continue;
    }

    if (isInformationalAssistantTool(message.toolName)) {
      continue;
    }

    hasSuccessfulMutationTool = true;
  }

  if (hasSuccessfulMutationTool && lastToolError) {
    return SYNTHETIC_ASSISTANT_PARTIAL_CONFIRMATION;
  }

  if (lastToolError) {
    return resolveAssistantErrorMessage(lastToolError);
  }

  if (hasSuccessfulMutationTool) {
    return SYNTHETIC_ASSISTANT_CONFIRMATION;
  }

  return undefined;
}

export function findLastAssistantMessageIndexAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): number {
  for (let index = messages.length - 1; index > lastUserIndex; index--) {
    if (messages[index]?.role === 'assistant') {
      return index;
    }
  }

  return -1;
}

// Skips synthetic messages (e.g. blog wizard narration) so a finalized model reply
// targets the model's own message rather than overwriting deterministic narration.
export function findLastModelAssistantMessageIndexAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): number {
  for (let index = messages.length - 1; index > lastUserIndex; index--) {
    const message = messages[index];
    if (message?.role === 'assistant' && !message.synthetic) {
      return index;
    }
  }

  return -1;
}

export function hasRunningBackgroundTaskAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): boolean {
  if (lastUserIndex === -1) {
    return false;
  }

  for (let index = lastUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (
      message?.role === 'tool' &&
      message.backgroundTaskStatus === 'running' &&
      message.toolResult === undefined
    ) {
      return true;
    }
  }

  return false;
}

function hasIncompleteAnalyzeExternalWebAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): boolean {
  if (lastUserIndex === -1) {
    return false;
  }

  for (let index = lastUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (message && isIncompleteAnalyzeExternalWebTool(message)) {
      return true;
    }
  }

  return false;
}

function collectSiteScopeToolsInActiveTurn(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): Message[] {
  const tools: Message[] = [];

  for (let index = lastUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (message?.role === 'user') {
      break;
    }

    if (
      message?.role === 'tool' &&
      isSiteScopeWebAnalysisToolName(message.toolName, message.toolInput ?? {})
    ) {
      tools.push(message);
    }
  }

  return tools;
}

function hasStreamingAssistantReplyAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): boolean {
  for (let index = lastUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (message?.role === 'user') {
      break;
    }

    if (message?.role === 'assistant' && message.streaming && message.content.trim()) {
      return true;
    }
  }

  return false;
}

export function hasSuccessfulInformationalToolAfterUser(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): boolean {
  if (lastUserIndex === -1) {
    return false;
  }

  for (let index = lastUserIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (
      message?.role === 'tool' &&
      !message.toolError &&
      message.toolResult !== undefined &&
      isInformationalAssistantTool(message.toolName) &&
      !isIncompleteAnalyzeExternalWebTool(message) &&
      !isFailedAnalyzeExternalWebTool(message)
    ) {
      return true;
    }
  }

  return false;
}

export function getLatestTurnFallbackText(
  messages: ReadonlyArray<Message>,
  isLoading: boolean,
): string | null {
  // Site assistant history restores user/assistant text and tool invocations.
  if (isLoading) {
    return null;
  }

  const lastUserIndex = findLastUserMessageIndex(messages);
  if (lastUserIndex === -1) {
    return null;
  }

  if (isSiteAssistantTurnOutstanding(messages, lastUserIndex)) {
    return null;
  }

  if (hasAssistantReplyAfterUser(messages, lastUserIndex)) {
    return null;
  }

  const turnReply = resolveTurnAssistantReplyContent(messages, lastUserIndex);
  if (turnReply) {
    return turnReply;
  }

  // Read-only tools aren't a reply. If no answer arrived, show a retry.
  if (hasSuccessfulInformationalToolAfterUser(messages, lastUserIndex)) {
    return SITE_ASSISTANT_REQUEST_FAILED;
  }

  return NO_RESPONSE_RECORDED_FALLBACK_TEXT;
}

export function finalizeSiteAssistantTurnMessages(messages: Message[]): Message[] {
  const lastUserIndex = findLastUserMessageIndex(messages);
  if (hasAssistantReplyAfterUser(messages, lastUserIndex)) {
    return messages;
  }

  if (isSiteAssistantTurnOutstanding(messages, lastUserIndex)) {
    return messages;
  }

  const turnReply = resolveTurnAssistantReplyContent(messages, lastUserIndex);
  if (turnReply) {
    return ensureAssistantReply(messages, turnReply);
  }

  // Read-only tools aren't a reply — ask the user to try again.
  if (hasSuccessfulInformationalToolAfterUser(messages, lastUserIndex)) {
    return ensureAssistantReply(messages, SITE_ASSISTANT_REQUEST_FAILED);
  }

  return messages;
}

export function reconcileMessagesAfterStreamFailure(
  messages: ReadonlyArray<Message>,
  resolvedErrorMessage: string,
): Message[] {
  const lastUserIndex = findLastUserMessageIndex(messages);
  // Must clear streaming before the reply check, or a partial answer stays "streaming".
  const updated = clearAssistantStreaming(removeEmptyAssistantMessagesInActiveTurn(messages));

  if (hasAssistantReplyAfterUser(updated, lastUserIndex)) {
    return updated;
  }

  // Edits may have landed, but the reply was cut off — don't claim full success.
  const turnReply = resolveTurnAssistantReplyContent(updated, lastUserIndex);
  if (turnReply === SYNTHETIC_ASSISTANT_CONFIRMATION) {
    return ensureAssistantReply(updated, SYNTHETIC_ASSISTANT_INCOMPLETE_CONFIRMATION);
  }
  if (turnReply) {
    return ensureAssistantReply(updated, turnReply);
  }

  return ensureAssistantReply(updated, resolvedErrorMessage);
}

export function clearAssistantStreaming(messages: ReadonlyArray<Message>): Message[] {
  let changed = false;

  const next = messages.map((message) => {
    if (message.role !== 'assistant' || !message.streaming) {
      return message;
    }

    changed = true;
    return { ...message, streaming: false };
  });

  return changed ? next : (messages as Message[]);
}

/**
 * True while this turn still has work left: a site analysis running, or waiting
 * for the assistant’s written answer after any successful site tool finishes.
 */
export function isSiteAssistantTurnOutstanding(
  messages: ReadonlyArray<Message>,
  lastUserIndex = findLastUserMessageIndex(messages),
): boolean {
  if (lastUserIndex === -1) {
    return false;
  }

  if (hasRunningBackgroundTaskAfterUser(messages, lastUserIndex)) {
    return true;
  }

  if (hasIncompleteAnalyzeExternalWebAfterUser(messages, lastUserIndex)) {
    return true;
  }

  const siteTools = collectSiteScopeToolsInActiveTurn(messages, lastUserIndex);
  if (siteTools.length === 0) {
    return false;
  }

  // All site tools are terminal here. Wait for synthesis when any succeeded.
  const anySuccess = siteTools.some((tool) => !isFailedAnalyzeExternalWebTool(tool));
  if (!anySuccess) {
    return false;
  }

  if (!hasAssistantReplyAfterUser(messages, lastUserIndex)) {
    return true;
  }

  return hasStreamingAssistantReplyAfterUser(messages, lastUserIndex);
}

function shouldTailForIncompleteActiveTurn(
  messages: ReadonlyArray<Message>,
  lastUserIndex: number,
): boolean {
  return (
    !hasAssistantReplyAfterUser(messages, lastUserIndex) ||
    hasStreamingAssistantReplyAfterUser(messages, lastUserIndex)
  );
}

/** Tail the thread while analyzeExternalWeb is still running or synthesis is pending. */
export function shouldTailSiteAssistantThread(messages: ReadonlyArray<Message>): boolean {
  const lastUserIndex = findLastUserMessageIndex(messages);
  if (lastUserIndex === -1) {
    return false;
  }

  if (!isSiteAssistantTurnOutstanding(messages, lastUserIndex)) {
    return false;
  }

  if (
    hasRunningBackgroundTaskAfterUser(messages, lastUserIndex) ||
    hasIncompleteAnalyzeExternalWebAfterUser(messages, lastUserIndex)
  ) {
    return shouldTailForIncompleteActiveTurn(messages, lastUserIndex);
  }

  return true;
}

/**
 * History reload can miss the latest user message (e.g. after a long site analysis
 * finishes on a reconnect). Put any missing live user rows back.
 */
export function stitchUserMessagesFromLive(
  memoryMessages: Message[],
  liveMessages: Message[],
): Message[] {
  if (liveMessages.length === 0) {
    return memoryMessages;
  }

  const liveUsers = liveMessages.filter(
    (message) => message.role === 'user' && message.content.trim().length > 0,
  );
  if (liveUsers.length === 0) {
    return memoryMessages;
  }

  const memoryUserContents = new Set(
    memoryMessages
      .filter((message) => message.role === 'user')
      .map((message) => message.content.trim())
      .filter((content) => content.length > 0),
  );

  const missingUsers = liveUsers.filter(
    (userMessage) => !memoryUserContents.has(userMessage.content.trim()),
  );
  if (missingUsers.length === 0) {
    return memoryMessages;
  }

  const result = [...memoryMessages];

  for (const userMessage of missingUsers) {
    const turnStartTimestamp = userMessage.timestamp;
    const insertAt = result.findIndex(
      (message) =>
        (message.role === 'tool' || message.role === 'assistant') &&
        message.timestamp >= turnStartTimestamp,
    );

    if (insertAt === -1) {
      result.push(userMessage);
      continue;
    }

    result.splice(insertAt, 0, userMessage);
  }

  return result;
}

/** Normalize messages when a stream or thread-tail settles. */
export function settleSiteAssistantMessages(messages: ReadonlyArray<Message>): Message[] {
  return removeEmptyAssistantMessagesInActiveTurn(
    clearAssistantStreaming(finalizeSiteAssistantTurnMessages([...messages])),
  );
}
